import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ShieldCheck, UserPlus, CheckCircle2, Copy, Send, Trash2, Search, RefreshCw, ExternalLink, ArrowRight } from 'lucide-react';
import { useAuth } from '../components/AuthGuard';
import { Link } from 'react-router-dom';

const generateRefId = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const rand = Math.floor(10000 + Math.random() * 90000);
    return `DIS-20${year}-${rand}`;
};

export default function OnboardingAccessManager() {
    const { user } = useAuth();
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    // Form fields
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [studentRef, setStudentRef] = useState(generateRefId());
    const [sendEmail, setSendEmail] = useState(true);

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'students'));
            const list = [];
            snap.forEach(d => {
                const data = d.data();
                if (data.status === 'Paid 1020' || data.status === 'Paid 1000' || data.gate === 1 || data.fee_status === 'PAID') {
                    list.push({ id: d.id, ...data });
                }
            });
            // Sort newest first
            list.sort((a, b) => new Date(b.approvedAt || b.addedAt || 0) - new Date(a.approvedAt || a.addedAt || 0));
            setCandidates(list);
        } catch (err) {
            console.error('Error fetching onboarding candidates:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidates();
    }, []);

    const handleGrantAccess = async (e) => {
        e.preventDefault();
        const cleanEmail = email.trim().toLowerCase();
        const cleanName = fullName.trim();
        const cleanRef = studentRef.trim() || generateRefId();

        if (!cleanEmail || !cleanName) {
            alert('Please provide both student email and full name.');
            return;
        }

        setSubmitting(true);
        setStatusMessage(null);

        try {
            const timestamp = new Date().toISOString();
            const verificationHash = 'MANUAL_VERIFIED_' + Date.now();

            // 1. Write to students collection
            await setDoc(doc(db, 'students', cleanEmail), {
                name: cleanName,
                fullName: cleanName,
                email: cleanEmail,
                phone: phone.trim() || '',
                status: 'Paid 1020',
                fee_status: 'PAID',
                gate: 1,
                applicationRef: cleanRef,
                studentId: cleanRef,
                cohort: `Class of ${new Date().getFullYear()}`,
                addedAt: timestamp,
                approvedAt: timestamp,
                approvedBy: user?.email || 'admin',
                addedVia: 'NexusOnboardingAccessManager'
            }, { merge: true });

            // 2. Write to onboarding collection
            await setDoc(doc(db, 'onboarding', cleanRef), {
                name: cleanName,
                email: cleanEmail,
                phone: phone.trim() || '',
                status: 'PAID',
                fee_status: 'PAID',
                gate: 1,
                applicationRef: cleanRef,
                vpre_ledger_hash: verificationHash,
                approvedAt: timestamp,
                approvedBy: user?.email || 'admin'
            }, { merge: true });

            // 3. Write to system_roles collection
            await setDoc(doc(db, 'system_roles', cleanEmail), {
                role: 'student',
                displayName: cleanName,
                status: 'active',
                gate: 1,
                payment_status: 'paid_onboarding',
                addedAt: timestamp,
                approvedBy: user?.email || 'admin'
            }, { merge: true });

            // 4. Optionally dispatch Brevo email
            let emailSent = false;
            if (sendEmail) {
                try {
                    const res = await fetch('https://dis-email-worker.moodstudiospace.workers.dev/send-paid-emails', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            studentId: cleanRef,
                            name: cleanName,
                            email: cleanEmail,
                            amount: '1,020',
                            hash: verificationHash
                        })
                    });
                    if (res.ok) emailSent = true;
                } catch (emailErr) {
                    console.warn('Email dispatch warning:', emailErr);
                }
            }

            const magicUrl = `https://dynamicindutechno.solutions/onboarding.html?studentId=${cleanRef}&email=${encodeURIComponent(cleanEmail)}&name=${encodeURIComponent(cleanName)}`;

            setStatusMessage({
                type: 'success',
                text: `Access granted for ${cleanName} (${cleanRef}).${emailSent ? ' Confirmation email dispatched.' : ''}`,
                link: magicUrl
            });

            // Reset form
            setEmail('');
            setFullName('');
            setPhone('');
            setStudentRef(generateRefId());
            fetchCandidates();

        } catch (err) {
            console.error('Error granting onboarding access:', err);
            setStatusMessage({
                type: 'error',
                text: 'Failed to grant access: ' + err.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2500);
    };

    const handleResendEmail = async (cand) => {
        const refId = cand.applicationRef || cand.studentId || cand.id;
        try {
            const res = await fetch('https://dis-email-worker.moodstudiospace.workers.dev/send-paid-emails', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    studentId: refId,
                    name: cand.name || cand.fullName || 'Student',
                    email: cand.email,
                    amount: '1,020',
                    hash: 'RESENT_VERIFIED_' + Date.now()
                })
            });
            if (res.ok) {
                alert(`Confirmation email resent to ${cand.email}`);
            } else {
                alert('Email worker error. Please verify worker status.');
            }
        } catch (e) {
            alert('Failed to send email: ' + e.message);
        }
    };

    const handleRevoke = async (candEmail) => {
        if (!window.confirm(`Revoke Gate 1 onboarding access for ${candEmail}?`)) return;
        try {
            await setDoc(doc(db, 'students', candEmail), {
                status: 'Revoked',
                fee_status: 'UNPAID',
                gate: 0
            }, { merge: true });
            await setDoc(doc(db, 'system_roles', candEmail), {
                payment_status: 'unpaid',
                status: 'inactive'
            }, { merge: true });
            fetchCandidates();
        } catch (e) {
            alert('Failed to revoke access: ' + e.message);
        }
    };

    const filtered = candidates.filter(c => {
        const query = searchQuery.toLowerCase();
        return (
            (c.email && c.email.toLowerCase().includes(query)) ||
            (c.name && c.name.toLowerCase().includes(query)) ||
            (c.applicationRef && c.applicationRef.toLowerCase().includes(query))
        );
    });

    return (
        <div className="space-y-8">
            {/* Navigation Tabs */}
            <div className="flex items-center gap-3 border-b border-zinc-800 pb-4">
                <Link
                    to="/onboarding-access"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#8B1A2D] text-white font-bold text-xs uppercase tracking-wider"
                >
                    <ShieldCheck size={16} />
                    Onboarding Form Access (Gate 1)
                </Link>
                <Link
                    to="/portal-access"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs uppercase tracking-wider transition-colors"
                >
                    <ExternalLink size={16} />
                    Site B Student Portal Access (Gate 2)
                </Link>
            </div>

            {/* Header */}
            <div>
                <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                    <UserPlus className="text-[#C9973A]" size={28} />
                    Onboarding Form Access Provisioning
                </h1>
                <p className="text-zinc-400 text-sm mt-1">
                    Directly whitelist candidate emails to unlock the DIS Student Onboarding Form, populate the central registry, and dispatch verified onboarding magic links.
                </p>
            </div>

            {/* Status Alert */}
            {statusMessage && (
                <div className={`p-4 rounded-xl border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
                    statusMessage.type === 'success'
                        ? 'bg-emerald-950/40 border-emerald-800 text-emerald-200'
                        : 'bg-red-950/40 border-red-800 text-red-200'
                }`}>
                    <div className="flex items-center gap-3">
                        <CheckCircle2 size={20} className={statusMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'} />
                        <span className="text-sm font-semibold">{statusMessage.text}</span>
                    </div>
                    {statusMessage.link && (
                        <button
                            type="button"
                            onClick={() => copyToClipboard(statusMessage.link, 'latest')}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-800 hover:bg-emerald-700 text-white text-xs font-bold whitespace-nowrap transition-colors"
                        >
                            <Copy size={14} />
                            {copiedId === 'latest' ? 'Copied Magic Link!' : 'Copy Magic Link'}
                        </button>
                    )}
                </div>
            )}

            {/* Quick Add Form Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl">
                <div className="text-xs font-black uppercase tracking-widest text-[#C9973A] mb-4">
                    Fast Provisioning Console
                </div>
                <form onSubmit={handleGrantAccess} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Candidate Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="e.g. candidate@gmail.com"
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Full Legal Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="e.g. LEKIREU THOMAS SENET"
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Phone Number
                            </label>
                            <input
                                type="text"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="e.g. +254 700 000 000"
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1 flex items-center justify-between">
                                <span>Reference ID</span>
                                <button
                                    type="button"
                                    onClick={() => setStudentRef(generateRefId())}
                                    className="text-zinc-500 hover:text-white"
                                    title="Generate new ID"
                                >
                                    <RefreshCw size={12} />
                                </button>
                            </label>
                            <input
                                type="text"
                                value={studentRef}
                                onChange={(e) => setStudentRef(e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm font-mono rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-2 gap-4 border-t border-zinc-800/80">
                        <label className="flex items-center gap-2 text-xs text-zinc-300 font-medium cursor-pointer">
                            <input
                                type="checkbox"
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                                className="w-4 h-4 rounded border-zinc-700 text-[#8B1A2D] focus:ring-[#8B1A2D] bg-zinc-800"
                            />
                            <span>Dispatch official Gate 1 clearance email with one-click magic link immediately</span>
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full sm:w-auto px-6 py-2.5 bg-[#8B1A2D] hover:bg-[#A32036] text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <RefreshCw className="animate-spin" size={16} />
                            ) : (
                                <ShieldCheck size={16} />
                            )}
                            <span>{submitting ? 'Granting Access...' : 'Grant Onboarding Access'}</span>
                        </button>
                    </div>
                </form>
            </div>

            {/* Filter and Live List */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-base font-bold text-white uppercase tracking-wider">
                            Authorized Onboarding Candidates ({candidates.length})
                        </h2>
                        <p className="text-zinc-500 text-xs">Candidates cleared to access the onboarding form</p>
                    </div>

                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Filter by name, email, or ID..."
                            className="w-full bg-zinc-800 border border-zinc-700 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-[#8B1A2D]"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="py-12 text-center text-zinc-500 text-sm">
                        Loading authorized candidates...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-12 text-center text-zinc-500 text-sm">
                        No authorized candidates found matching your filter.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-zinc-800/60 text-zinc-400 uppercase font-black tracking-wider border-b border-zinc-800">
                                <tr>
                                    <th className="py-3 px-4">Candidate Name</th>
                                    <th className="py-3 px-4">Email</th>
                                    <th className="py-3 px-4">Reference ID</th>
                                    <th className="py-3 px-4">Status</th>
                                    <th className="py-3 px-4">Authorized Date</th>
                                    <th className="py-3 px-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800 text-zinc-300">
                                {filtered.map((c) => {
                                    const ref = c.applicationRef || c.studentId || c.onboardingId || 'DIS-2026-PENDING';
                                    const magicLink = `https://dynamicindutechno.solutions/onboarding.html?studentId=${ref}&email=${encodeURIComponent(c.email || c.id)}&name=${encodeURIComponent(c.name || c.fullName || '')}`;
                                    const isCopied = copiedId === (c.email || c.id);

                                    return (
                                        <tr key={c.id} className="hover:bg-zinc-800/30 transition-colors">
                                            <td className="py-3 px-4 font-bold text-white">
                                                {c.name || c.fullName || 'Candidate'}
                                            </td>
                                            <td className="py-3 px-4 font-mono text-zinc-400">
                                                {c.email || c.id}
                                            </td>
                                            <td className="py-3 px-4 font-mono text-[#C9973A]">
                                                {ref}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-950 text-emerald-400 border border-emerald-800">
                                                    {c.status || 'Paid 1020'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-zinc-500">
                                                {c.approvedAt ? new Date(c.approvedAt).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <div className="inline-flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(magicLink, c.email || c.id)}
                                                        className="px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-bold flex items-center gap-1 transition-colors"
                                                        title="Copy magic onboarding link"
                                                    >
                                                        <Copy size={12} />
                                                        <span>{isCopied ? 'Copied!' : 'Copy Link'}</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleResendEmail(c)}
                                                        className="p-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                                                        title="Resend Gate 1 clearance email"
                                                    >
                                                        <Send size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRevoke(c.email || c.id)}
                                                        className="p-1 rounded bg-zinc-800 hover:bg-red-950 text-zinc-500 hover:text-red-400 transition-colors"
                                                        title="Revoke access"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
